/**
 * SegnalazioneErroreModal — F-007 Phase 4 (IMPL-P4-005).
 *
 * Modale (semplice overlay HTML — il design system non espone ancora `Dialog`
 * shadcn) che permette all'operatore di marcare una pratica come `BLOCCATA`.
 *
 * Form:
 *   - `CheckboxErrorCategories` (multi-select da SEGNALAZIONE_REASONS)
 *   - textarea ALTRO (visibile solo se ALTRO selezionato, max 500 char)
 *   - `EmailPreview` live-rendered con i campi correnti
 *
 * Submit:
 *   - disabilitato se `reasons.length === 0`
 *   - disabilitato se ALTRO selezionato e `detail` vuoto/whitespace
 *   - chiama `useSegnalaErrore().mutate({ praticaId, input })` e su success
 *     invoca `onClose()`
 *
 * Accessibilità:
 *   - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` puntato sul
 *     titolo. ESC chiude (tramite onKeyDown sull'overlay).
 */
import * as React from 'react';
import type { SegnalazioneInput, SegnalazioneReason } from '@app/shared';
import { Button } from '@/components/ui/button';
import { useSegnalaErrore } from '../hooks/useSegnalaErrore';
import { CheckboxErrorCategories } from './CheckboxErrorCategories';
import { EmailPreview } from './EmailPreview';

interface SegnalazioneErroreModalProps {
  praticaId: string;
  open: boolean;
  onClose: () => void;
  /**
   * Nome cliente — usato solo nell'EmailPreview. La detail page lo passa
   * dalla risposta `getDetail`.
   */
  clientName?: string;
}

const MAX_DETAIL = 500;

export function SegnalazioneErroreModal({
  praticaId,
  open,
  onClose,
  clientName = '',
}: SegnalazioneErroreModalProps) {
  const [reasons, setReasons] = React.useState<SegnalazioneReason[]>([]);
  const [detail, setDetail] = React.useState('');
  const mutation = useSegnalaErrore();

  // Reset stato all'apertura — niente carry-over fra pratiche.
  React.useEffect(() => {
    if (open) {
      setReasons([]);
      setDetail('');
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const altroSelected = reasons.includes('ALTRO');
  const detailMissing = altroSelected && detail.trim().length === 0;
  const isSubmitDisabled =
    mutation.isPending || reasons.length === 0 || detailMissing;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    const input: SegnalazioneInput = {
      reasons,
      detail: altroSelected ? detail.trim() : null,
    };
    mutation.mutate(
      { praticaId, input },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="segnalazione-title"
        className="w-full max-w-2xl rounded-lg border bg-background p-6 shadow-lg"
      >
        <header className="mb-4">
          <h2
            id="segnalazione-title"
            className="text-lg font-semibold tracking-tight"
          >
            Segnala errore
          </h2>
          <p className="text-sm text-muted-foreground">
            La pratica passerà in stato <strong>BLOCCATA</strong> e il cliente
            riceverà un'email con le motivazioni indicate.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-3">
              <p className="text-sm font-medium">Categorie</p>
              <CheckboxErrorCategories
                selected={reasons}
                onChange={setReasons}
                disabled={mutation.isPending}
              />
              {altroSelected && (
                <div className="space-y-1">
                  <label
                    htmlFor="segnalazione-detail"
                    className="text-sm font-medium"
                  >
                    Dettaglio (obbligatorio)
                  </label>
                  <textarea
                    id="segnalazione-detail"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                    rows={4}
                    maxLength={MAX_DETAIL}
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                    disabled={mutation.isPending}
                  />
                  <p className="text-xs text-muted-foreground">
                    {detail.length}/{MAX_DETAIL}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium">Anteprima email</p>
              <EmailPreview
                clientName={clientName}
                praticaId={praticaId}
                selectedReasons={reasons}
                detail={detail}
              />
            </div>
          </div>

          {mutation.isError && (
            <p
              role="alert"
              className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              Impossibile inviare la segnalazione. Riprova.
            </p>
          )}

          <footer className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              variant="destructive"
              aria-disabled={isSubmitDisabled}
              disabled={isSubmitDisabled}
            >
              {mutation.isPending ? 'Invio in corso…' : 'Invia segnalazione'}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
